/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { DialogDefaultV2 } from '../../../components/v2';
import { TIPO_PROGRAMACAO_DIARIA_INSPECAO } from '../../../constants/ids';
import {
  amostraRestante,
  formatItemDescricao,
  getResumoPorTipo,
  labelNsirs,
} from './helpers';

const GrupoResumo = ({ grupo }) => {
  const qtd = Number(grupo.quantidade) || 0;
  const tipo = grupo.tipo || 'Outros';
  const rest = amostraRestante(grupo);
  const isInspecao = grupo.tipoId === TIPO_PROGRAMACAO_DIARIA_INSPECAO;
  const nsirs = Array.isArray(grupo.nsirs) ? grupo.nsirs : [];
  const itens = Array.isArray(grupo.itens) ? grupo.itens : [];

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
        {qtd}× {tipo}
      </Typography>

      {isInspecao && nsirs.length > 0 && (
        <Stack spacing={0.5} sx={{ pl: 0.5 }}>
          {nsirs.map(nsir => (
            <Box key={nsir.id || labelNsirs(nsir)}>
              <Typography variant="body2">
                {labelNsirs(nsir)}
                {nsir.equipamento && nsir.codigo ? ` · ${nsir.equipamento}` : ''}
              </Typography>
              {nsir.tipoServico && (
                <Typography variant="caption" color="text.secondary">
                  {nsir.tipoServico}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      )}

      {!isInspecao && itens.length > 0 && (
        <Stack spacing={0.5} sx={{ pl: 0.5 }}>
          {itens.map(item => {
            const resumo = formatItemDescricao(item);
            const hasResumo = resumo && resumo !== '-';
            if (!hasResumo && !item.descricao) return null;

            return (
              <Box key={item.id || `${item.tipoId}-${item.veiculoId}-${item.cidadeDe}`}>
                {hasResumo && (
                  <Typography variant="body2">{resumo}</Typography>
                )}
                {item.descricao && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.descricao}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      {!isInspecao && !itens.length && !nsirs.length && (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5 }}>
          Sem amostra neste resumo.
        </Typography>
      )}

      {rest > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 0.5 }}>
          e mais {rest} no dia
        </Typography>
      )}
    </Box>
  );
};

GrupoResumo.propTypes = {
  grupo: PropTypes.object.isRequired,
};

const ResumoDiaModal = ({ open, row, onClose }) => {
  const grupos = getResumoPorTipo(row);
  const totalItens = row?.totalItens;
  const inspetor = row?.inspetorName || row?.inspetorNome || '-';
  const data = row?.referenceDayFormat || row?.referenceDay || '-';

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title="Resumo do dia"
      maxWidth="sm"
      fullWidth
      readOnly
    >
      <Stack spacing={2} sx={{ mt: 0.5 }}>
        <Box>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>Inspetor: </Box>
            {inspetor}
          </Typography>
          {(row?.inspetorAuxiliarName || row?.inspetorAuxiliarNome) && (
            <Typography variant="body2">
              <Box component="span" sx={{ fontWeight: 700 }}>Ajudante/Observador: </Box>
              {row.inspetorAuxiliarName || row.inspetorAuxiliarNome}
            </Typography>
          )}
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 700 }}>Data: </Box>
            {data}
          </Typography>
          {totalItens != null && (
            <Typography variant="body2" color="text.secondary">
              {totalItens} item{totalItens === 1 ? '' : 's'} no dia
            </Typography>
          )}
        </Box>

        <Divider />

        {grupos.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum resumo por tipo disponível.
          </Typography>
        ) : (
          <Stack spacing={2} divider={<Divider flexItem />}>
            {grupos.map((grupo, index) => (
              <GrupoResumo key={grupo.tipoId || `${grupo.tipo}-${index}`} grupo={grupo} />
            ))}
          </Stack>
        )}
      </Stack>
    </DialogDefaultV2>
  );
};

ResumoDiaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

ResumoDiaModal.defaultProps = {
  row: null,
};

export default ResumoDiaModal;
